import Paper from "@mui/material/Paper";
import InputBase from "@mui/material/InputBase";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Divider,
  InputAdornment,
} from "@mui/material";
import SplitButton from "./SplitButton";

function ModernSearchBar({
  placeHolder,
  handleSearchClick,
  onKeyDown,
  searchOption,
  searchType,
  setSearchType,
  activeTerm,
  onClear,
  total,
}) {
  const [searchTerm, setSearchTerm] = useState(activeTerm ?? "");

  // keep the input in step when the search is reset from outside (e.g. empty state)
  useEffect(() => {
    setSearchTerm(activeTerm ?? "");
  }, [activeTerm]);

  const handleClear = () => {
    setSearchTerm("");
    if (onClear) onClear();
    else handleSearchClick?.("");
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1.5,
        p: 1.5,
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flex: "1 1 320px",
          maxWidth: 560,
          height: 40,
          borderRadius: 2,
          border: 1,
          borderColor: "divider",
          overflow: "hidden",
          transition: "border-color 0.2s, box-shadow 0.2s",
          "&:focus-within": {
            borderColor: "primary.main",
            boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
          },
        }}
      >
        {searchOption && (
          <>
            <Select
              size="small"
              variant="standard"
              disableUnderline
              value={searchType ?? ""}
              onChange={(e) => setSearchType?.(e.target.value)}
              inputProps={{ "aria-label": "Search by" }}
              renderValue={(v) =>
                `Search by: ${
                  searchOption.find((o) => o.id === v)?.display ?? ""
                }`
              }
              sx={{
                pl: 1.5,
                pr: 0.5,
                height: "100%",
                fontSize: 13,
                fontWeight: 600,
                whiteSpace: "nowrap",
                bgcolor: "action.hover",
                "& .MuiSelect-select": { py: 0 },
              }}
            >
              {searchOption.map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  {o.display}
                </MenuItem>
              ))}
            </Select>
            <Divider orientation="vertical" flexItem />
          </>
        )}
        <InputBase
          fullWidth
          placeholder={placeHolder}
          value={searchTerm}
          onKeyDown={onKeyDown}
          onChange={(e) => setSearchTerm(e.target.value)}
          inputProps={{ "aria-label": placeHolder }}
          startAdornment={
            <InputAdornment position="start" sx={{ ml: 1.5 }}>
              <IconButton
                size="small"
                onClick={() => handleSearchClick(searchTerm)}
                aria-label="search"
              >
                <SearchIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          }
          endAdornment={
            searchTerm ? (
              <InputAdornment position="end" sx={{ mr: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={handleClear}
                  aria-label="clear search"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null
          }
          sx={{ fontSize: 14 }}
        />
      </Box>
      {activeTerm ? (
        <Typography variant="body2" color="text.secondary" sx={{ ml: "auto" }}>
          Results for “{activeTerm}”
          {total !== undefined ? ` · ${total}` : ""}
        </Typography>
      ) : null}
    </Box>
  );
}

export default function SearchBar({ sx, variant = "default", ...props }) {
  if (variant === "modern") return <ModernSearchBar {...props} />;
  return <DefaultSearchBar sx={sx} {...props} />;
}

function DefaultSearchBar({ sx, ...props }) {
  const {
    placeHolder,
    handleSearchClick,
    onKeyDown,
    searchOption,
    searchType,
    setSearchType,
  } = props;
  const [searchTerm, setSearchTerm] = useState("");

  return (
    <Paper sx={{ display: "flex" }}>
      {searchOption && (
        <Box sx={{ display: "flex", p: 1, gap: 1 }}>
          <Typography
            variant="caption"
            sx={(theme) => ({
              display: "inline-flex",
              alignItems: "center",
              height: "auto",
              px: 1.2,
              borderRadius: 1.5,
              borderColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.12)"
                  : "rgba(0,0,0,0.15)",

              /*backgroundColor:
            theme.palette.mode === "dark" ? "#2a2a2a" : "#f5f5f5",*/

              color: theme.palette.text.primary,
              fontWeight: 600,
              fontSize: 12,
              letterSpacing: 0.5,
            })}
          >
            Search Option
          </Typography>
          <SplitButton
            options={searchOption}
            value={searchType}
            onChange={setSearchType}
          />
        </Box>
      )}
      <Box
        sx={(theme) => ({
          width: "30%",
          m: 1,
          display: "flex",
          alignItems: "center",
          px: 1.2,
          height: 36,
          borderRadius: 1.5,
          border: "1px solid",
          borderColor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.12)"
              : "rgba(0,0,0,0.15)",
          backgroundColor:
            theme.palette.mode === "dark" ? "#2a2a2a" : "#f5f5f5",
          transition: "0.2s",

          "&:focus-within": {
            borderColor: "primary.main",
            backgroundColor:
              theme.palette.mode === "dark" ? "#303030" : "#ffffff",
          },
        })}
      >
        <InputBase
          fullWidth
          placeholder={placeHolder}
          value={searchTerm}
          onKeyDown={onKeyDown}
          onChange={(e) => setSearchTerm(e.target.value)}
          inputProps={{ "aria-label": placeHolder }}
          sx={{
            fontSize: 13,
            ml: 0.5,
          }}
        />

        <IconButton
          size="small"
          onClick={() => handleSearchClick(searchTerm)}
          aria-label="search"
          sx={(theme) => ({
            p: 0.8,
            color: theme.palette.text.secondary,
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.06)",
            },
          })}
        >
          <SearchIcon fontSize="small" />
        </IconButton>
      </Box>
    </Paper>
  );
}
